import type { ChangeEvent, FocusEvent, MouseEvent, ReactNode } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled, { createGlobalStyle } from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'

import TextInput from 'components/Common/Form/Input/TextInput'
import Caption from 'LuxKit/Typography/Caption'
import InputText from 'LuxKit/Typography/InputText'
import TextButton from 'LuxKit/Button/TextButton'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import LineEditIcon from 'LuxKit/Icons/line/LineEditIcon'
import Counter, { type CounterChangeEventHandler } from 'LuxKit/Counter'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import type { TripStop } from 'tripPlanner/api/types'
import { formatStopDateRange } from 'tripPlanner/utils/chatDates'
import { appendContextTag, TRIP_PLANNER_ACTION_CONTEXT_LABEL } from 'tripPlanner/utils/contextTag'
import { STOP_TIMING_EDITOR_DROPDOWN_Z_INDEX } from './stopTimingLayers'

interface Props {
  stop: TripStop
  children: ReactNode
  onSendMessage: (message: string) => void
  disabled?: boolean
  className?: string
}

type StopTimingEditIntent =
  | { type: 'set-dates'; startDate: string; endDate: string; durationDayCount: number }
  | { type: 'clear-dates'; durationDayCount: number }
  | { type: 'update-duration'; durationDayCount: number }

const TriggerButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${rem(4)};
  min-width: 0;
  padding: 0;
  border: 0;
  border-bottom: 1px dashed transparent;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: start;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    border-bottom-color: currentColor;
  }

  &:disabled {
    cursor: default;
    opacity: 0.7;
    border-bottom-color: transparent;
  }
`

const TriggerContent = styled.span`
  min-width: 0;
`

const EditIconWrap = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  opacity: 0.72;
`

const FormContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  width: ${rem(280)};
  padding: ${rem(16)};
`

const DateInputs = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: ${rem(12)};
`

const ClearDatesRow = styled.div`
  margin-top: ${rem(-4)};
`

const DurationField = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${rem(4)};
`

const DurationCounter = styled(Counter)`
  width: ${rem(140)};
  justify-content: space-between;

  > .variant-text-regular {
    min-width: ${rem(56)};
    white-space: nowrap;
  }
`

const FieldLabel = styled(InputText)`
  display: block;
`

const StopTimingEditorDropdownLayer = createGlobalStyle`
  [data-stop-timing-editor-dropdown] {
    z-index: ${STOP_TIMING_EDITOR_DROPDOWN_Z_INDEX};
  }
`

function isIsoDate(value: string): boolean {
  return moment(value, ISO_DATE_FORMAT, true).isValid()
}

function getDurationDayCount(startDate: string, endDate: string): number | undefined {
  if (!isIsoDate(startDate) || !isIsoDate(endDate)) return undefined

  const start = moment(startDate, ISO_DATE_FORMAT, true)
  const end = moment(endDate, ISO_DATE_FORMAT, true)
  if (end.isBefore(start, 'day')) return undefined

  return end.diff(start, 'days') + 1
}

function getEndDateForDuration(startDate: string, durationDayCount: number): string | undefined {
  if (!isIsoDate(startDate)) return undefined
  return moment(startDate, ISO_DATE_FORMAT, true)
    .add(durationDayCount - 1, 'days')
    .format(ISO_DATE_FORMAT)
}

function getExistingStopDurationDayCount(stop: TripStop): number | undefined {
  if (stop.startDate && stop.endDate) {
    return getDurationDayCount(stop.startDate, stop.endDate) ?? 1
  }

  const daysCount = stop.days?.length ?? 0
  return daysCount > 0 ? daysCount : undefined
}

function getDraftStopDurationDayCount(stop: TripStop): number {
  return getExistingStopDurationDayCount(stop) ?? 1
}

function formatDurationLabel(dayCount: number): string {
  return `${dayCount} ${dayCount === 1 ? 'day' : 'days'}`
}

function formatDateRangeOrFallback(startDate: string | undefined, endDate: string | undefined): string {
  if (startDate && endDate && isIsoDate(startDate) && isIsoDate(endDate)) {
    return formatStopDateRange(startDate, endDate)
  }

  return 'not set'
}

function openNativeDatePicker(event: FocusEvent<HTMLInputElement> | MouseEvent<HTMLInputElement>) {
  try {
    event.currentTarget.showPicker?.()
  } catch {
    // Browsers may reject showPicker outside trusted user activation.
  }
}

function buildStopTimingEditMessage(stop: TripStop, intent: StopTimingEditIntent, visibleMessage: string): string {
  const previousRange = formatDateRangeOrFallback(stop.startDate, stop.endDate)
  const previousStartDate = stop.startDate ?? 'not set'
  const previousEndDate = stop.endDate ?? 'not set'
  const previousDuration = getExistingStopDurationDayCount(stop)
  const contextPrefix = [
    'The customer used the stop days and dates editor in the itinerary UI.',
    `Target stop_id=${stop.id} "${stop.placeName}".`,
    `Current values: date range ${previousRange} (startDate=${previousStartDate}, endDate=${previousEndDate}, duration=${previousDuration ? formatDurationLabel(previousDuration) : 'not set'}).`,
  ].join(' ')
  const nextInstruction = (() => {
    if (intent.type === 'set-dates') {
      return `Set the stop dates to ${formatStopDateRange(intent.startDate, intent.endDate)} (startDate=${intent.startDate}, endDate=${intent.endDate}, duration=${formatDurationLabel(intent.durationDayCount)}).`
    }

    if (intent.type === 'clear-dates') {
      return `Remove the stop startDate and endDate, and keep the stop duration as ${formatDurationLabel(intent.durationDayCount)}.`
    }

    return `Update the stop duration to ${formatDurationLabel(intent.durationDayCount)} without setting startDate or endDate.`
  })()

  return appendContextTag(
    visibleMessage,
    [
      contextPrefix,
      nextInstruction,
      'Adjust neighbouring stops and itinerary dates as needed so the full trip remains coherent.',
    ].join(' '),
    TRIP_PLANNER_ACTION_CONTEXT_LABEL,
  )
}

function StopTimingEditControl({ stop, children, onSendMessage, disabled = false, className }: Props) {
  const intl = useIntl()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)
  const [startDate, setStartDate] = useState(stop.startDate ?? '')
  const [endDate, setEndDate] = useState(stop.endDate ?? '')
  const [durationDayCount, setDurationDayCount] = useState(() => getDraftStopDurationDayCount(stop))

  const resetDraft = useCallback(() => {
    setStartDate(stop.startDate ?? '')
    setEndDate(stop.endDate ?? '')
    setDurationDayCount(getDraftStopDurationDayCount(stop))
  }, [stop])

  useEffect(() => {
    if (open) return
    resetDraft()
  }, [open, resetDraft])

  const openDropdown = useCallback(() => {
    if (disabled) return
    setOpen(true)
  }, [disabled])

  const closeDropdown = useCallback(() => {
    setOpen(false)
  }, [])

  const handleStartDateChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setStartDate(event.currentTarget.value)
  }, [])

  const handleEndDateChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setEndDate(event.currentTarget.value)
  }, [])

  const handleDurationDayCountChange = useCallback<CounterChangeEventHandler>((value) => {
    setDurationDayCount(value)
    const nextEndDate = getEndDateForDuration(startDate, value)
    if (nextEndDate) {
      setEndDate(nextEndDate)
    }
  }, [startDate])

  const clearDates = useCallback(() => {
    const currentDurationDayCount = getDurationDayCount(startDate, endDate)
    if (currentDurationDayCount) {
      setDurationDayCount(currentDurationDayCount)
    }
    setStartDate('')
    setEndDate('')
  }, [endDate, startDate])

  const hasStartDate = Boolean(startDate)
  const hasEndDate = Boolean(endDate)
  const hasNoDates = !hasStartDate && !hasEndDate
  const hasCompleteDateRange = hasStartDate && hasEndDate
  const dateRangeDayCount = hasCompleteDateRange ? getDurationDayCount(startDate, endDate) : undefined
  const dateRangeIsValid = hasNoDates || Boolean(dateRangeDayCount)
  const effectiveDurationDayCount = dateRangeDayCount ?? durationDayCount
  const existingDurationDayCount = getExistingStopDurationDayCount(stop)
  const datesChanged = startDate !== (stop.startDate ?? '') || endDate !== (stop.endDate ?? '')
  const durationChanged = effectiveDurationDayCount !== existingDurationDayCount
  const canSave = dateRangeIsValid && (datesChanged || durationChanged)

  const intent = useMemo<StopTimingEditIntent | undefined>(() => {
    if (!canSave || !effectiveDurationDayCount) return undefined
    if (dateRangeDayCount) {
      return { type: 'set-dates', startDate, endDate, durationDayCount: dateRangeDayCount }
    }
    if (datesChanged && hasNoDates) {
      return { type: 'clear-dates', durationDayCount: effectiveDurationDayCount }
    }
    return { type: 'update-duration', durationDayCount: effectiveDurationDayCount }
  }, [canSave, dateRangeDayCount, datesChanged, effectiveDurationDayCount, endDate, hasNoDates, startDate])

  const visibleMessage = useMemo(() => {
    if (!intent) return ''

    if (intent.type === 'set-dates') {
      return intl.formatMessage(
        {
          id: 'GmPadHCwUTP6pT8V_Mce',
          defaultMessage: 'Update {placeName} dates to {dateRange}',
          description: 'Chat message sent when the user edits a stop date range in AI TripPlanner',
        },
        { placeName: stop.placeName, dateRange: formatStopDateRange(intent.startDate, intent.endDate) },
      )
    }

    if (intent.type === 'clear-dates') {
      return intl.formatMessage(
        {
          id: 'luacJSmQ6f8RoPcJDTQ4',
          defaultMessage: 'Remove dates for {placeName}',
          description: 'Chat message sent when the user clears stop dates in AI TripPlanner',
        },
        { placeName: stop.placeName },
      )
    }

    return intl.formatMessage(
      {
        id: 'JF1xIYX9bQzJSbUtTzbS',
        defaultMessage: 'Update {placeName} to {duration}',
        description: 'Chat message sent when the user edits a stop duration without dates in AI TripPlanner',
      },
      { placeName: stop.placeName, duration: formatDurationLabel(intent.durationDayCount) },
    )
  }, [intent, intl, stop.placeName])

  const handleSave = useCallback(() => {
    if (!canSave || !intent) return
    onSendMessage(buildStopTimingEditMessage(stop, intent, visibleMessage))
    closeDropdown()
  }, [canSave, closeDropdown, intent, onSendMessage, stop, visibleMessage])

  const editLabel = intl.formatMessage(
    {
      id: 'hZ1ob9L2hg1ZiJyodP5i',
      defaultMessage: 'Edit days and dates for {placeName}',
      description: 'Accessible label for editing stop days and dates in AI TripPlanner',
    },
    { placeName: stop.placeName },
  )

  const durationLabel = intl.formatMessage({
    id: 'DcjBOoCbwGUg3DIt6yal',
    defaultMessage: 'Duration',
    description: 'Duration summary label for editing stop days and dates in AI TripPlanner',
  })

  return (
    <>
      <StopTimingEditorDropdownLayer />
      <TriggerButton
        type="button"
        ref={triggerRef}
        onClick={openDropdown}
        disabled={disabled}
        aria-label={editLabel}
        className={className}
      >
        <TriggerContent>{children}</TriggerContent>
        <EditIconWrap aria-hidden="true">
          <LineEditIcon size="XS" />
        </EditIconWrap>
      </TriggerButton>
      <DropdownList
        size="S"
        open={open}
        onClose={closeDropdown}
        triggerRef={triggerRef}
        anchorRef={triggerRef}
        placement="bottom-start"
        portaled
        data-stop-timing-editor-dropdown
        title={intl.formatMessage({
          id: 'ImQxQPVqJrvOvKMRvvDD',
          defaultMessage: 'Edit days and dates',
          description: 'Dropdown heading for editing stop days and dates in AI TripPlanner',
        })}
        primaryActionProps={{
          children: intl.formatMessage({
            id: 'W-B8RA3oaApbUDIEz9QY',
            defaultMessage: 'Save',
            description: 'Primary action label for saving stop days and dates in AI TripPlanner',
          }),
          onClick: handleSave,
          disabled: !canSave,
        }}
        secondaryActionProps={{
          children: intl.formatMessage({
            id: 'pptWzWz2mC6fG-qZvzAn',
            defaultMessage: 'Cancel',
            description: 'Secondary action label for cancelling stop days and dates in AI TripPlanner',
          }),
          onClick: closeDropdown,
          kind: 'tertiary',
        }}
      >
        <FormContent>
          <DurationField>
            <FieldLabel variant="label">
              {durationLabel}
            </FieldLabel>
            <DurationCounter
              value={effectiveDurationDayCount}
              min={1}
              max={365}
              onChange={handleDurationDayCountChange}
              size="large"
              valueFormatter={formatDurationLabel}
              data-testid="stop-duration-counter"
            />
          </DurationField>
          <DateInputs>
            <TextInput
              type="date"
              label={intl.formatMessage({
                id: 'X3uLmaA_A_xoEGU0dmpc',
                defaultMessage: 'Start',
                description: 'Start date input label for editing a stop date range in AI TripPlanner',
              })}
              value={startDate}
              onChange={handleStartDateChange}
              onClick={openNativeDatePicker}
              onFocus={openNativeDatePicker}
              noValidationSpacing
            />
            <TextInput
              type="date"
              label={intl.formatMessage({
                id: 'KXNc1ezaqS83q1l0Gk3b',
                defaultMessage: 'End',
                description: 'End date input label for editing a stop date range in AI TripPlanner',
              })}
              value={endDate}
              onChange={handleEndDateChange}
              onClick={openNativeDatePicker}
              onFocus={openNativeDatePicker}
              min={startDate || undefined}
              manualError={Boolean(hasCompleteDateRange && !dateRangeIsValid)}
              invalidErrorMessage={intl.formatMessage({
                id: 'w9PNAoQHGtEnOrNW6vgG',
                defaultMessage: 'End date must be on or after start date',
                description: 'Validation error for an invalid stop date range in AI TripPlanner',
              })}
              noValidationSpacing
            />
          </DateInputs>
          {(stop.startDate || stop.endDate || startDate || endDate) && (
            <ClearDatesRow>
              <TextButton kind="tertiary" size="small" onClick={clearDates}>
                {intl.formatMessage({
                  id: 'Ms7LDUkZiY7pfNJQGAGU',
                  defaultMessage: 'Remove dates',
                  description: 'Action label for clearing stop dates in AI TripPlanner',
                })}
              </TextButton>
            </ClearDatesRow>
          )}
          {!dateRangeIsValid && hasCompleteDateRange && (
            <Caption variant="medium" colour="critical">
              {intl.formatMessage({
                id: 'daj9thHEOBrKomMaOOk7',
                defaultMessage: 'Choose an end date on or after the start date.',
                description: 'Inline validation message for editing a stop date range in AI TripPlanner',
              })}
            </Caption>
          )}
          {(hasStartDate || hasEndDate) && !hasCompleteDateRange && (
            <Caption variant="medium" colour="critical">
              {intl.formatMessage({
                id: 'f-UUnIBQF8-7__fPrfn_',
                defaultMessage: 'Choose both a start and end date, or leave both blank.',
                description: 'Inline validation message for partially filled stop dates in AI TripPlanner',
              })}
            </Caption>
          )}
        </FormContent>
      </DropdownList>
    </>
  )
}

export default StopTimingEditControl
